// HFS a11y (demand item b): Enter on a variant option (radio/checkbox) must never submit the add-to-cart
// form through implicit submission. Enter selects the option, the same as Space. Capture phase, bound once.
if (!window.hfsOptionEnterGuard) {
  window.hfsOptionEnterGuard = true;
  document.addEventListener(
    'keydown',
    (evt) => {
      if (evt.key !== 'Enter' || evt.isComposing) return;
      const el = evt.target;
      if (!(el instanceof HTMLInputElement) || !['radio', 'checkbox'].includes(el.type)) return;
      const inProductForm = el.form && el.form.closest('product-form');
      const inPicker = el.closest(
        'variant-radios, variant-selects, .custom-variant-picker, #sub-variant-options, fieldset.product-form__input'
      );
      if (!inProductForm && !inPicker) return;
      evt.preventDefault();
      if (el.type === 'radio' && !el.disabled) el.click();
    },
    true
  );
  // Clear the "Please select" error state of a group once one of its options is chosen.
  document.addEventListener('change', (evt) => {
    const el = evt.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return;
    const group = el.closest('fieldset');
    if (!group) return;
    group
      .querySelectorAll('input[type="radio"][aria-invalid]')
      .forEach((r) => r.removeAttribute('aria-invalid'));
    // HFS a11y (PDP-44): hide the group's message and drop the aria-describedby that pointed at it
    if (window.hfsPdp) window.hfsPdp.setOptionError(el.dataset.optionName || el.name, false, group);
    else {
      const message = group.querySelector('[data-option-error]');
      if (message) {
        message.style.display = 'none';
        group.querySelectorAll('input[type="radio"]').forEach((radio) => {
          const description = (radio.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== message.id).join(' ');
          if (description) radio.setAttribute('aria-describedby', description);
          else radio.removeAttribute('aria-describedby');
        });
      }
    }
  });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton.disabled) {
          this.submitButton.disabled = false;
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('data-hfs-unavailable', '');
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // HFS a11y: only an activation of this form's own Add to cart button may add, and only once at a time
        // (blocks the injected sold-out button and repeated Enter presses from creating extra cart lines).
        if (this.isSubmitting) return;
        if (evt.submitter !== this.submitButton) return;

        // HFS (final sweep): 1) an unanswered option group of this section's picker is reported first, so a partial
        // selection (which also marks the button "Unavailable") says what is missing instead of doing nothing.
        if (this.showMissingOptions()) return;

        // 2) HFS a11y (PDP-02): a sold-out/unavailable combination (aria-disabled Add button) never adds. Repeat the
        // reason in the live region, because activating the button otherwise gives no feedback.
        if (this.submitButton.hasAttribute('data-hfs-unavailable')) {
          let reason = (this.submitButton.querySelector('span') || this.submitButton).textContent.trim();
          // HFS (FBL-005): never announce the stale "Add to cart" label for a refused submit.
          if (reason === ((window.variantStrings && window.variantStrings.addToCart) || 'Add to cart') || reason === 'Add to cart') reason = 'This option is not available';
          if (window.hfsPdp) window.hfsPdp.announce(reason || (window.variantStrings && window.variantStrings.soldOut));
          return;
        }

        // 3) Validate the form for blank value (this form's own variant id, not the first one on the page)
        const idInput = this.form.querySelector('[name=id]');
        var variantIdValue = idInput ? idInput.value.trim() : '';
        if (variantIdValue === "") {
          this.handleErrorMessage('Please select all product options before adding to cart.');
          return;
        }
        
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // The form uses novalidate for custom option errors, so validate its associated quantity explicitly.
        const quantity = this.form.elements.namedItem('quantity');
        if (quantity && (!quantity.value || !Number.isInteger(Number(quantity.value)) || Number(quantity.value) < 1 || !quantity.checkValidity())) {
          this.handleErrorMessage(quantity.validationMessage || 'Enter a valid quantity of at least 1.', true);
          quantity.setAttribute('aria-invalid', 'true');
          quantity.focus();
          return;
        }
        if (quantity) quantity.removeAttribute('aria-invalid');

        this.handleErrorMessage();
        this.error = false;

        this.isSubmitting = true;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        //this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        let overlaySpinner = this.querySelector('.loading-overlay__spinner');
        if (overlaySpinner) {
          overlaySpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        let added = false;
        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok && !data.status) data.status = response.status || 500;
            return data;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || window.cartStrings.error);
              return;
            }
            added = true;
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            if (added) {
              // The add succeeded but its section render failed. Never repeat the mutation to recover the UI.
              window.location.assign(window.routes.cart_url);
              return;
            }
            this.handleErrorMessage('Could not confirm whether this item was added. Check your cart before trying again.', true);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.submitButton.classList.remove('loading');
            if (!this.error && !this.submitButton.hasAttribute('data-hfs-unavailable')) this.submitButton.removeAttribute('aria-disabled');
            const spinner = this.querySelector('.loading-overlay__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      // Shows "Please select an option" for every radio group of this section's variant picker with nothing checked,
      // focuses the first one and returns true. Product cards (no picker with this section id) are never blocked.
      // Error messages are tagged data-option-error="<name>" (each with its own unique id) and sit after the
      // group's <legend>; the radios point at the message with aria-describedby while it shows.
      showMissingOptions() {
        const picker = document.getElementById(`variant-radios-${this.dataset.sectionId}`);
        if (!picker) return false;
        const groups = {};
        picker.querySelectorAll('input[type="radio"][name]').forEach((radio) => {
          (groups[radio.name] = groups[radio.name] || []).push(radio);
        });
        let firstMissing = null;
        Object.keys(groups).forEach((name) => {
          const radios = groups[name];
          const missing = !radios.some((radio) => radio.checked);
          const hasMessage = window.hfsPdp && window.hfsPdp.setOptionError(radios[0].dataset.optionName || name, missing, radios[0].closest('fieldset'));
          if (!hasMessage) {
            const message = radios[0].closest('fieldset')?.querySelector('[data-option-error]');
            if (message) message.style.display = missing ? '' : 'none';
            radios.forEach((radio) => {
              if (missing) {
                radio.setAttribute('aria-invalid', 'true');
                if (message) radio.setAttribute('aria-describedby', message.id);
              }
              else radio.removeAttribute('aria-invalid');
            });
          }
          if (missing && !firstMissing) firstMissing = radios.find((radio) => !radio.disabled) || radios[0];
        });
        if (firstMissing) firstMissing.focus();
        return !!firstMissing;
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force && errorMessage) return;
        // HFS a11y (JS-08): never fail silently; replace Shopify's technical "missing id" text with a plain message.
        if (errorMessage === "Required parameter missing or invalid: id") {
          errorMessage = 'Please select all product options before adding to cart.';
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        // HFS (FBL-005): a Retry button added by global.js belongs to its own message only.
        this.errorMessageWrapper.querySelectorAll('.hfs-refresh-retry').forEach((button) => button.remove());
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
